import { mockJobPositions, type JobPosition } from '../mocks/careers';
import { isServerBackendEnabled } from './env';
import { fetchStateRowsRequest } from './serverApi';
import { STORAGE_KEYS, readStoredJSON, writeStoredJSON } from './state/appState';

function isLegacyDemoJobPosition(item: JobPosition) {
  const matchingDemo = mockJobPositions.find((demoItem) => demoItem.id === item.id);
  if (!matchingDemo) {
    return false;
  }

  return (
    matchingDemo.title === item.title &&
    matchingDemo.department === item.department &&
    matchingDemo.location === item.location &&
    matchingDemo.type === item.type &&
    matchingDemo.description === item.description &&
    JSON.stringify(matchingDemo.requirements) === JSON.stringify(item.requirements) &&
    matchingDemo.postedAt === item.postedAt
  );
}

export function sanitizeStoredJobPositions(items: JobPosition[]) {
  return items.filter((item) => !isLegacyDemoJobPosition(item));
}

export function loadStoredJobPositions() {
  return sanitizeStoredJobPositions(readStoredJSON<JobPosition[]>(STORAGE_KEYS.jobPositions, []));
}

export function loadActiveJobPositions() {
  return loadStoredJobPositions().filter((item) => item.isActive);
}

export function saveStoredJobPositions(items: JobPosition[]) {
  writeStoredJSON(STORAGE_KEYS.jobPositions, sanitizeStoredJobPositions(items));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('zobu-job-positions-updated'));
  }
}

export async function syncJobPositionsFromServer() {
  if (!isServerBackendEnabled) {
    return loadStoredJobPositions();
  }

  const response = await fetchStateRowsRequest([STORAGE_KEYS.jobPositions]);
  const row = response.rows.find((entry) => entry.storageKey === STORAGE_KEYS.jobPositions);
  if (!row) {
    return loadStoredJobPositions();
  }

  const parsed = sanitizeStoredJobPositions(JSON.parse(row.payloadText) as JobPosition[]);
  writeStoredJSON(STORAGE_KEYS.jobPositions, parsed);
  return parsed;
}

export function sanitizeLegacyJobPositionsInStorage() {
  if (typeof window === 'undefined') {
    return false;
  }

  const storedRaw = readStoredJSON<JobPosition[]>(STORAGE_KEYS.jobPositions, []);
  const sanitized = sanitizeStoredJobPositions(storedRaw);
  const nextPayload = JSON.stringify(sanitized);

  if (window.localStorage.getItem(STORAGE_KEYS.jobPositions) === nextPayload) {
    return false;
  }

  window.localStorage.setItem(STORAGE_KEYS.jobPositions, nextPayload);
  window.dispatchEvent(new CustomEvent('zobu-job-positions-updated'));
  return true;
}
